// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CustomEvent } from '@shared/utils/CustomEvents';
import { Resources } from '@shared/localizations/Resources';
import { LinkedSystem } from '../misc/LinkedSystem';
import { KeyCode, KeyEventProcessor } from '@shared/commons/KeyEventProcessor';
import InformerPanelTopTemplate from '@front/templates/InformerPanelTopTemplate.html';
import { Infor } from '../cache/BaseInformer';
import { TerceraInstrumentLookupDropDownForm } from '../elements/Lookup/TerceraInstrumentLookupDropDownForm';
import { ColouringModes } from '../elements/QuickTable/QuickTableColumn';
import { TerceraQuickTreeEvents } from '../elements/QuickTree/TerceraQuickTree';
import { TerceraLinkControlConstants } from '../UtilsClasses/TerceraLinkControlConstants';
import { ExternalScreen } from '../screen/ExternalScreen';
import { MessageBoxType } from '@shared/utils/MessageBoxUtils';
import { TerceraMessageBox } from '../screen/TerceraMessageBox';
import { TerceraQuickInstrumentLookupScreen } from '../screen/TerceraQuickInstrumentLookupScreen';
import { PanelLocKeys, PanelNames } from '../UtilsClasses/FactoryConstants';
import { LookupDropDownShowParams } from '../UtilsClasses/LookupDropDownShowParams';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { DynProperty } from '@shared/commons/DynProperty';
import { InterTraderBtnStatus } from '@shared/utils/Instruments/InterTraderBtnStatus';
import { DataCache } from '@shared/commons/DataCache';
import { TerceraSymbolLookupBaseDataProvider } from '@shared/commons/NoNFixedListCore';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { CustomerAccess } from '@shared/commons/CustomerAccess/CustomerAccess';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { TerceraLookup } from '../elements/Lookup/TerceraLookup';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import { Md5 } from 'ts-md5';
import $ from 'jquery';
import { HotkeysManager } from '@shared/commons/cache/Hotkeys/HotkeysManager';
import { InformerPanelActionHotkeysEnum } from '@shared/commons/cache/Hotkeys/NamesEventHotkey ';
import { createTagDictionary, NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
import { WatchlistEnum } from '@shared/utils/Enums/WatchlistEnum';
export class InformerPanel extends ApplicationPanelWithTable {
    get wlComboBox() {
        return this.Controls.wlComboBox;
    }
    constructor() {
        super();
        this.headerLocaleKey = 'panel.informer';
        this.SymbolInfoPanelAllowed = false;
        this.OnInstrumentChange = null;
        this.moreThanOneTTKey = 'panel.watchlist.menu.MoreThanOneSymbolSelected.tt'; // #95439
        this.noOneTTKey = 'panel.watchlist.menu.NoSymbolSelected.tt';
        this.quickInstrumentSelector = null;
        this._selectedInstrumentListName = '';
        this.dataProvider = new TerceraSymbolLookupBaseDataProvider();
        this.OnInstrumentChange = new CustomEvent();
    }
    getType() {
        return PanelNames.InformerPanel;
    }
    oninit() {
        super.oninit();
        this.on('onAdd', this.onAdd);
        this.observe('isAccountLinkShow', (link) => {
            if (!link) {
                void this.set('account', DataCache.getPrimaryAccount());
            }
        });
    }
    oncomplete() {
        super.oncomplete();
        this.SymbolInfoPanelAllowed = CustomerAccess.panelAllowed(PanelNames.SymbolInfoPanel);
        this.populateTableContextMenu();
        const qt = this.getQuickTable();
        const index = isNullOrUndefined(this.InitGroupByColumnIndex)
            ? WatchlistEnum.TYPE_COL_INDEX
            : this.InitGroupByColumnIndex;
        const column = qt.columns[index];
        if (!isNullOrUndefined(column) && !column.hidden && column.allowGrouping) {
            qt.setGroupByColumnIdx(index);
            qt.createGroupByContextMenuItem(qt.columns);
        }
        qt.OnSelectionChanged.Subscribe(this.selectionChange, this);
        qt.OnPaintedPictureButtonClick.Subscribe(this.onPaintedPictureButtonClick, this);
        this.quickTableRactive.on(TerceraQuickTreeEvents.DoubleClick, this.onDoubleClick.bind(this));
        this.wlComboBox.OnSelectList.Subscribe(this.selectInstrumentList, this);
        this.wlComboBox.observe('items', this.updateContextMenu.bind(this));
        KeyEventProcessor.OnKeyDown.Subscribe(this.onGlobalKeyDown, this);
        this.quickInstrumentSelector = null;
        if (!this.deserializeDelayedData()) {
            this.wlComboBox.onSymbolsChanged();
        }
    }
    repopulate() {
        const qt = this.getQuickTable();
        this.visibleColumnChanged(qt.getVisibleColumn());
        const rows = qt.rowsArray;
        const len = rows.length;
        let resArr = '';
        for (let i = 0; i < len; i++) {
            resArr = resArr + rows[i].item.InstrumentName() + ';';
        }
        qt.ClearAll();
        void this.addInstrumentsFromString(resArr);
    }
    onGlobalKeyDown() {
        if (!this.get('focused'))
            return;
        const keyProc = KeyEventProcessor;
        if (keyProc.currentButton !== KeyCode.DELETE || keyProc.WithModButtons()) {
            return;
        }
        this.onRemoveSelected();
    }
    dispose() {
        KeyEventProcessor.OnKeyDown.UnSubscribe(this.onGlobalKeyDown, this);
        this.wlComboBox.OnSelectList.UnSubscribe(this.selectInstrumentList, this);
        super.dispose();
    }
    localize() {
        super.localize();
        this.set({
            addInsBtnTooltip: Resources.getResource('panel.watchlist.addInstrumentsButton.tooltip')
        });
    }
    onAdd() {
        const obj = {};
        TerceraLookup.prototype.setItems.bind(obj)(DataCache.getInstruments(false));
        const params = new LookupDropDownShowParams();
        // SET ITEMS HERE
        params.items = obj.items;
        params.callBack = this.instrumentsDropDownCallback.bind(this);
        params.isMultiSelect = true;
        // params.isMultiSelectMode = true;
        params.isCentered = true;
        params.autoClose = true;
        params.parentPanel = this;
        params.dataProvider = this.dataProvider;
        TerceraInstrumentLookupDropDownForm.ShowForm(params);
    }
    preparePopup() {
        super.preparePopup();
        const removeAllMenuItem = this.menuTagDict[InformerPanel.REMOVE_ALL];
        removeAllMenuItem.enabled = this.getQuickTable().rowsArray.length > 0;
    }
    updatePanelHeader() {
        void this.set({
            header: Resources.getResource(this.headerLocaleKey) +
                (this.NeedCalculateRowCount ? '  (' + this.rowCount + ')' : '')
        });
    }
    AddInstrumentToTable(instrument) {
        const qtRactive = this.quickTableRactive;
        const qt = !isNullOrUndefined(qtRactive) ? qtRactive.quickTable : null;
        if (isNullOrUndefined(qt)) {
            return;
        }
        const dc = DataCache;
        const sess = SessionSettings;
        const rowDict = qt.rows;
        const insFullName = instrument.GetInteriorID();
        const row = rowDict[insFullName];
        if (isNullOrUndefined(row)) {
            const item = new Infor(insFullName, dc, sess, this);
            qt.AddItem(item);
        }
    }
    OnQuickTableEditingInfoMapChanged(itemID, newInfo) {
        var _a, _b, _c;
        const cell = (_c = (_b = (_a = this.getQuickTable()) === null || _a === void 0 ? void 0 : _a.getItemById(itemID)) === null || _b === void 0 ? void 0 : _b.cells) === null || _c === void 0 ? void 0 : _c[0];
        if (isNullOrUndefined(cell))
            return;
        cell.QuickTableEditingInfo = newInfo;
        cell.ReadOnly = isNullOrUndefined(newInfo);
        cell.isDirty = true;
    }
    selectInstrumentList(symbolIds, listName) {
        const shouldSkipSelectionUpdate = this._selectedInstrumentListName === listName &&
            symbolIds.length === this.getQuickTable().getDataRowsCount();
        if (shouldSkipSelectionUpdate)
            return;
        this._selectedInstrumentListName = listName;
        this.onRemoveAll(true);
        const symbolIdsString = symbolIds.join(';');
        void this.addInstrumentsFromString(symbolIdsString);
    }
    onInstrumentListChange() {
        const rows = this.getQuickTable().rowsArray;
        const symbolIds = [];
        for (const row of rows) {
            if (!isNullOrUndefined(row.item)) {
                symbolIds.push(row.id);
            }
        }
        this.wlComboBox.setSelectedItemSymbols(symbolIds);
    }
    onRemoveSelected() {
        const qtRactive = this.quickTableRactive;
        const qt = !isNullOrUndefined(qtRactive) ? qtRactive.quickTable : null;
        if (!isNullOrUndefined(qt)) {
            qt.RemoveSelectedItems();
        }
        this.onInstrumentListChange();
    }
    getSelectedInstrumentID() {
        const ins = this.getInstrument();
        let result = '';
        if (!isNullOrUndefined(ins)) {
            result = ins.GetInteriorID();
        }
        return result;
    }
    onRemoveAll(skipUpdate) {
        const qtRactive = this.quickTableRactive;
        const qt = !isNullOrUndefined(qtRactive) ? qtRactive.quickTable : null;
        if (isNullOrUndefined(qt))
            return;
        qt.ClearRows();
        this.updatePanelHeader();
        if (!skipUpdate) {
            this.onInstrumentListChange();
        }
    }
    onDoubleClick() {
        const instrumentID = this.getSelectedInstrumentID();
        if (isValidString(instrumentID)) {
            // Open OE.
            MainWindowManager.Factory.addPanel(PanelNames.AdvancedOrderEntry, null, function (panel) {
                panel.symbolLink_In(instrumentID);
            });
        }
        else {
            // Open Lookup.
            this.onAdd();
        }
    }
    updateContextMenu() {
        const newMenuItems = this.wlComboBox.menuItems;
        const symbolListsMenuItem = this.menuTagDict[InformerPanel.SYMBOL_LISTS];
        symbolListsMenuItem.subitems = newMenuItems;
    }
    populateTableContextMenu() {
        let items = [];
        items.push({
            type: NewContextMenuItemType.Default,
            text: 'panel.watchlist.menu.AddSymbol',
            svgImage: 'add',
            event: this.onAdd.bind(this)
        });
        this.AddSymbolInfoContextMenuItemIfNeed(items, false);
        items = items.concat([
            {
                type: NewContextMenuItemType.Default,
                text: 'panel.watchlist.menu.RemoveInstrument',
                enabled: false,
                event: this.onRemoveSelected.bind(this),
                tag: InformerPanel.REMOVESYMBOL
            },
            {
                type: NewContextMenuItemType.Default,
                text: 'panel.watchlist.menu.clearAll',
                event: this.onRemoveAll.bind(this, false),
                enabled: false,
                tag: InformerPanel.REMOVE_ALL
            },
            {
                type: NewContextMenuItemType.Separator
            },
            {
                type: NewContextMenuItemType.Default,
                text: 'panel.watchlist.menu.SymbolLists',
                subitems: this.wlComboBox.menuItems,
                tag: InformerPanel.SYMBOL_LISTS
            }
        ]);
        items = this.AddOpeningPanelsCM(items);
        this.menuTagDict = createTagDictionary(items);
        this.getQuickTable().setTableContextMenuItems(items);
        this.localize();
    }
    GetOpeningPanelsCMLocKeysSet() {
        const keysObj = {};
        keysObj[ApplicationPanelWithTable.OPEN_CHART] = 'panel.watchlist.menu.Chart';
        keysObj[ApplicationPanelWithTable.OPEN_MD] = 'panel.watchlist.menu.MarketDepth';
        keysObj[ApplicationPanelWithTable.OPEN_TS] = 'panel.watchlist.menu.TimeSales';
        keysObj[ApplicationPanelWithTable.OPEN_OE] = 'panel.watchlist.menu.OrderEntry';
        return keysObj;
    }
    selectionChange() {
        const qt = this.getQuickTable();
        const selectedRowsId = qt.selectedRowIds;
        if (!isValidArray(selectedRowsId)) {
            return;
        }
        let ins = '';
        const ids = selectedRowsId;
        if (ids.length > 0) {
            const row = qt.rows[ids[0]];
            ins = row.item.InstrumentName();
        }
        this.symbolLink_Out(this.isNewSubscriber(), ins);
        const enabled = !!ins;
        const menuTagDict = this.menuTagDict;
        const removeMenuItem = menuTagDict[InformerPanel.REMOVESYMBOL];
        removeMenuItem.enabled = enabled;
        this.SetOpeningPanelsCMEnability(menuTagDict, enabled);
    }
    onPaintedPictureButtonClick(args, event) {
        // if (data.controlType === DynProperty.DELAYED_PICTURE_RIGHT_AND_TEXT)    // #112155 -> при клики на delayed icon пока никаких действий, только отображение тултипа по ховеру
        // {
        // //     // App.mainWindow.ShowSymbolInfo(data.row.item.instrument, { X: event.pageX, Y: event.pageY })
        //     return
        // }
        if (args.controlType === DynProperty.ITCHART_ADVANCED) {
            const tool = DataCache.ExternalLinksCache.getITChartAdvancedExtLink();
            const link = this.getITChartAdvancedLink();
            if (isValidString(link)) {
                if (tool.clientUserInternalBrowser) {
                    void ExternalScreen.Show({
                        Text: link,
                        GetRefreshURL: this.getITChartAdvancedLink.bind(this),
                        ScreenName: 'ITChart Advanced',
                        Aliases: null,
                        Tool: tool
                    });
                }
                else {
                    window.open(link);
                }
            }
            return;
        }
        if (args.controlType === DynProperty.ORDER_BUTTON || args.controlType === DynProperty.TRADE_BUTTON) {
            const ins = this.getInstrument();
            const insStr = !isNullOrUndefined(ins) ? ins.GetInteriorID() : '';
            const isTrade = args.controlType === DynProperty.TRADE_BUTTON;
            if (!isNullOrUndefined(ins) &&
                ins.GetInterTraderButtonStatus(isTrade).status !== InterTraderBtnStatus.Open) {
                return;
            }
            const orderType = OrderType[isTrade ? 'Market' : 'Limit'];
            MainWindowManager.Factory.addPanel(PanelNames.AdvancedOrderEntry, null, function (panel) {
                panel.symbolLink_In(insStr);
                panel.selectOrderType(orderType);
            });
        }
    }
    isNewSubscriber() {
        const color = this.get('symbolLinkValue');
        if (color === TerceraLinkControlConstants.STATE_NONE) {
            return false;
        }
        return !isValidString(LinkedSystem.getSymbol(color));
    }
    symbolLink_Out(newSubscriber, instrumentName) {
        if (isNullOrUndefined(instrumentName)) {
            return;
        }
        const color = this.get('symbolLinkValue');
        if (color !== TerceraLinkControlConstants.STATE_NONE) {
            LinkedSystem.setSymbol(color, instrumentName, newSubscriber);
        }
        this.OnInstrumentChange.Raise(instrumentName);
    }
    callBack(newProperties) {
        super.callBack(newProperties);
        const dp = DynProperty.getPropertyByName(newProperties, 'selectedTemplate');
        if (isValidString(dp === null || dp === void 0 ? void 0 : dp.value) && !isNullOrUndefined(this.wlComboBox)) {
            this.wlComboBox.restoreCorrectSelectItem(dp.value);
        }
        else {
            this.wlComboBox.onSymbolsChanged();
        }
    }
    Properties() {
        const properties = super.Properties();
        if (!isNullOrUndefined(this.wlComboBox)) {
            const selectedItemName = this.wlComboBox.selectedItemName;
            if (isValidString(selectedItemName)) {
                properties.push(new DynProperty('selectedTemplate', selectedItemName, DynProperty.STRING, DynProperty.HIDDEN_GROUP));
            }
        }
        return properties;
    }
    addInstrumentsFromString(instrumentsString) {
        return __awaiter(this, void 0, void 0, function* () {
            // '' need for refresh quickTable
            if (isNullOrUndefined(instrumentsString)) {
                return;
            }
            const instruments = instrumentsString.split(';').filter(Boolean);
            const instrArray = [];
            for (let i = 0; i < instruments.length; i++) {
                const instrument = yield this.dataProvider.getInstrumentByName(instruments[i]);
                if (!isNullOrUndefined(instrument)) {
                    instrArray.push(instrument);
                }
            }
            this.responseFillRows(instrArray);
        });
    }
    instrumentsDropDownCallback(listHelpers) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!Array.isArray(listHelpers)) {
                return;
            }
            const instrArray = [];
            for (let i = 0; i < listHelpers.length; i++) {
                const helper = listHelpers[i];
                const instrument = yield this.dataProvider.getInstrument(helper);
                if (!isNullOrUndefined(instrument)) {
                    instrArray.push(instrument);
                }
            }
            this.responseFillRows(instrArray);
            this.onInstrumentListChange();
        });
    }
    responseFillRows(instruments) {
        const qt = this.getQuickTable();
        if (isNullOrUndefined(qt)) {
            return;
        }
        const allowedRowsNum = MainWindowManager.Factory.GetThrottlingOperationValue(this.getType(), true).rowsNumber;
        let alreadyAddedNum = qt.getDataRowsCount();
        for (const ins of instruments) {
            const interiorId = ins.GetInteriorID();
            if (!isNullOrUndefined(qt.rows[interiorId])) {
                continue;
            }
            if (allowedRowsNum !== -1 && alreadyAddedNum >= allowedRowsNum) {
                return TerceraMessageBox.Show(Resources.getResource('workspace.information'), Resources.getResource('Rows.firstPart') +
                    ' ' +
                    Resources.getResource(PanelLocKeys[this.getType()]) +
                    ' ' +
                    Resources.getResource('Rows.secondPart'), MessageBoxType.Info, null, null, false, true);
            }
            this.AddInstrumentToTable(ins);
            alreadyAddedNum++;
        }
    }
    _onKeyDownProcess(event) {
        const DOMElement = $(event.node.parentNode.parentNode);
        const w = DOMElement.width();
        const h = DOMElement.height();
        const pos = DOMElement.position();
        let posX = w / 2 + pos.left - TerceraQuickInstrumentLookupScreen.WIDTH / 2;
        posX = posX < 5 ? 5 : posX;
        const posY = h / 2 + pos.top - TerceraQuickInstrumentLookupScreen.HEIGHT / 2;
        this.quickInstrumentSelector = TerceraQuickInstrumentLookupScreen.Show(posX, posY, event, this.instrumentsDropDownCallback.bind(this), this.onAdd.bind(this), this);
    }
    getITChartAdvancedLink() {
        var _a;
        const tool = DataCache.ExternalLinksCache.getITChartAdvancedExtLink();
        const ins = this.getInstrument();
        if (isNullOrUndefined(tool) || isNullOrUndefined(ins)) {
            return null;
        }
        const insTradableID = ins.InstrumentTradableID;
        const acc = DataCache.getUserByLogin(DataCache.UserLogin).Accounts[0];
        const userID = ((_a = acc.ExtendedFields) === null || _a === void 0 ? void 0 : _a.ITChartAdvanced) ? acc.ExtendedFields.ITChartAdvanced : acc.userID;
        const locale = 'en_GB';
        const MD5Key = Md5.hashStr(tool.itAdvancedPrivateKey + userID + locale + insTradableID);
        const link = tool.urlStr +
            '?uid=' +
            userID +
            '&key=' +
            MD5Key +
            '&locale=' +
            locale +
            '&id=' +
            insTradableID +
            '&gmt=' +
            DateTimeConvertor.getClientTimeZoneOffset() +
            '&theme=night';
        return link;
    }
    SetColumnsDefaultDisplayIndex(table) {
        table.columns[13].displayedIndex = 1;
    }
    SetColumnsColouringMode(table) {
        this.ProcessSetColumnsColouringMode(table, [3], ColouringModes.Previous);
        this.ProcessSetColumnsColouringMode(table, [12, 13], ColouringModes.Signed);
    }
    HotkeyPressed(hotkey) {
        const isPressed = super.HotkeyPressed(hotkey);
        if (isPressed) {
            return true;
        }
        const hotkeyAction = HotkeysManager.GetActionByHotkeyForPanel(hotkey, this.getType());
        switch (hotkeyAction) {
            case InformerPanelActionHotkeysEnum.CopySelectedSymbols: {
                const listInstruments = this.getSelectedInstruments();
                if (listInstruments.length === 0)
                    return false;
                const names = listInstruments.map((ins) => ins.DisplayName());
                const text = names.join('\n');
                void navigator.clipboard.writeText(text);
                return true;
            }
            case InformerPanelActionHotkeysEnum.PasteSelectedSymbols: {
                void navigator.clipboard.readText().then((text) => {
                    if (isValidString(text)) {
                        void this.populateTableFromList(text);
                    }
                });
                return true;
            }
            default:
                return false;
        }
    }
    populateTableFromList(text) {
        return __awaiter(this, void 0, void 0, function* () {
            const list = yield DataCache.GetInstrumentsByDisplayName(text);
            if (list.length === 0)
                return;
            for (const instrument of list) {
                this.AddInstrumentToTable(instrument);
                this.onInstrumentListChange();
            }
        });
    }
}
InformerPanel.REMOVESYMBOL = 'removeSymbol';
InformerPanel.REMOVE_ALL = 'REMOVE_ALL';
InformerPanel.SYMBOL_LISTS = 'SYMBOL_LISTS';
ApplicationPanelWithTable.extendWith(InformerPanel, {
    data: function () {
        return {
            isSymbolLinkShow: true,
            isAccountLinkShow: true,
            canFilterByAccount: false,
            isEnabledCombobox: true,
            editableCBtooltip: 'editableComboBox.tooltip',
            items: [],
            addInsBtnTooltip: ''
        };
    },
    partials: {
        bodyPartial: InformerPanelTopTemplate
    }
});
